import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Link, Navigate } from 'react-router-dom';
import { ElectricityPrice, PriceArea } from './types';
import { fetchElectricityPrices, getPriceAreaInfo, historicalPrices } from './utils/api';
import { calculateElectricitySubsidy } from './utils/subsidyCalculator';
import { format, parseISO, getMonth } from 'date-fns';
import { nb } from 'date-fns/locale';
import NorgesPrisCalculator from './components/NorgesPrisCalculator';
import DayOverview from './components/DayOverview';
import GoogleAd from './components/GoogleAd';
import Header from './components/Header';
import Footer from './components/Footer';
import PriceAreaSelector from './components/PriceAreaSelector';
import DateSelector from './components/DateSelector';
import PriceList from './components/PriceList';
import PriceChart from './components/PriceChart';
import ElectricityUsageCalculator from './components/ElectricityUsageCalculator';
import PriceAlerts from './components/PriceAlerts';
import BestTimeToUse from './components/BestTimeToUse';
import WeeklyTrend from './components/WeeklyTrend';
import SEO from './components/SEO';
import ScrollToTop from './components/ScrollToTop';
import Blog from './pages/Blog';
import BlogPost from './pages/BlogPost';
import PrivacyPolicy from './pages/personvernerklaering';
import TermsOfService from './pages/bruksvilkar';
import NorgesPris from './pages/Norgespris';
import Unsubscribe from './components/Unsubscribe';
import ProviderComparison from './pages/ProviderComparison';
import MorningReport from './components/MorningReport';
import { Info, Zap, Clock, Percent, ListFilter, Calculator, ChevronUp, ChevronDown, MapPin } from 'lucide-react';
import { CheckCircle } from 'lucide-react';
import SidebarAd from './components/SidebarAd';

function HomePage() {
  const [selectedArea, setSelectedArea] = useState<PriceArea>('NO1');
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  const [prices, setPrices] = useState<ElectricityPrice[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [isHeaderExpanded, setIsHeaderExpanded] = useState(false);

  useEffect(() => {
    const fetchPrices = async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await fetchElectricityPrices(selectedDate, selectedArea);
        setPrices(data);
      } catch (err) {
        setError('Kunne ikke hente prisdata. Vennligst prøv igjen senere.');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchPrices();
  }, [selectedArea, selectedDate]);

  // Find the current hour's price
  const now = new Date();
  const currentPrice = prices.find(
    (price) => 
      new Date(price.time_start) <= now && 
      new Date(price.time_end) > now
  );

  // Calculate subsidy for current price
  const currentPriceValue = currentPrice?.NOK_per_kWh || null;
  const subsidyInfo = currentPriceValue ? calculateElectricitySubsidy(currentPriceValue, selectedArea) : null;
  const priceAfterSubsidy = subsidyInfo?.isSubsidyActive ? subsidyInfo.priceAfterSubsidy : null;

  // Find lowest and highest price of the day
  const lowestPrice = prices.length > 0 
    ? prices.reduce((min, p) => p.NOK_per_kWh < min.NOK_per_kWh ? p : min, prices[0]) 
    : null;
  
  const highestPrice = prices.length > 0 
    ? prices.reduce((max, p) => p.NOK_per_kWh > max.NOK_per_kWh ? p : max, prices[0]) 
    : null;

  const currentIndex = prices.findIndex(
    (p) => parseISO(p.time_start) <= now && parseISO(p.time_end) > now
  );
  
  const futurePrices = prices.slice(currentIndex + 1);
  
  // Find next significant price change
  const nextCheaperHour = futurePrices.find(p => p.NOK_per_kWh < currentPrice?.NOK_per_kWh * 0.9);
  const nextExpensiveHour = futurePrices.find(p => p.NOK_per_kWh > currentPrice?.NOK_per_kWh * 1.1);
  
  const waitTimeHours = nextCheaperHour
    ? (parseISO(nextCheaperHour.time_start).getTime() - now.getTime()) / (1000 * 60 * 60)
    : null;
    
  const priceChangeHours = nextExpensiveHour
    ? (parseISO(nextExpensiveHour.time_start).getTime() - now.getTime()) / (1000 * 60 * 60)
    : null;

  // Calculate price position relative to day's range
  const priceRange = (highestPrice && lowestPrice) 
    ? highestPrice.NOK_per_kWh - lowestPrice.NOK_per_kWh 
    : 0;
  
  const pricePosition = (priceRange && currentPriceValue && lowestPrice) 
    ? (currentPriceValue - lowestPrice.NOK_per_kWh) / priceRange 
    : 0.5;
    
  // Get previous month's average for comparison
  const months = Object.keys(historicalPrices);
  const currentMonthIndex = months.indexOf(format(now, 'MMMM'));
  const previousMonth = currentMonthIndex > 0 ? months[currentMonthIndex - 1] : months[months.length - 1];
  const historicalAverage = historicalPrices[previousMonth]?.[selectedArea] / 100; // Convert øre to NOK
  
  // Calculate percentage difference from historical average
  const historicalDiff = historicalAverage && currentPriceValue
    ? ((currentPriceValue - historicalAverage) / historicalAverage) * 100
    : null;
    
  // Calculate average price for the next 6 hours
  const nextSixHours = prices
    .filter(p => parseISO(p.time_start) > now)
    .slice(0, 6);
  
  const avgNextSixHours = nextSixHours.length > 0
    ? nextSixHours.reduce((sum, p) => sum + p.NOK_per_kWh, 0) / nextSixHours.length
    : null;
    
  const priceTrend = avgNextSixHours && currentPriceValue
    ? ((avgNextSixHours - currentPriceValue) / currentPriceValue) * 100
    : null;
    
  let bannerStyle = '';
  let bannerMessage = '';
  let additionalTip = '';
  
  if (currentPriceValue) {
    if (currentPriceValue < 0.30) {
      bannerStyle = 'bg-gray-50 text-gray-800';
      if (nextExpensiveHour && priceChangeHours !== null && priceChangeHours < 3) {
        bannerMessage = `Hei! Billig strøm fram til kl ${format(parseISO(nextExpensiveHour.time_start), 'HH:mm', { locale: nb })}. Perfekt tidspunkt for å lade bilen eller kjøre en vask.`;
        additionalTip = '💡 Tips: Start ladingen nå for å få mest mulig ut av de lave prisene!';
      } else {
        bannerMessage = `Hei! Strømmen holder seg billig hele timen. Fint tidspunkt for lading og andre strømkrevende aktiviteter.`;
        additionalTip = '💡 Tips: Sjekk prislisten under for å se når prisene eventuelt endrer seg.';
      }
    } else if (currentPriceValue > 0.90) {
      bannerStyle = 'bg-gray-50 text-gray-800';
      if (nextCheaperHour && waitTimeHours !== null && waitTimeHours < 3) {
        bannerMessage = `Hei! Høye priser nå, men blir billigere fra kl ${format(parseISO(nextCheaperHour.time_start), 'HH:mm', { locale: nb })}. Vent med ladingen hvis du kan.`;
        additionalTip = '💡 Tips: Utsett energikrevende aktiviteter til prisene faller.';
      } else {
        bannerMessage = 'Hei! Dette er en av dagens dyreste perioder. Vent med lading hvis mulig.';
        additionalTip = '💡 Tips: Sjekk prislisten under for å finne bedre tidspunkter.';
      }
    } else if (currentPriceValue > 0.60) {
      bannerStyle = 'bg-gray-50 text-gray-800';
      if (nextCheaperHour && waitTimeHours !== null && waitTimeHours < 3) {
        bannerMessage = `Hei! Litt høye priser nå. Blir billigere fra kl ${format(parseISO(nextCheaperHour.time_start), 'HH:mm', { locale: nb })}.`;
        additionalTip = '💡 Tips: Vurder å vente med lading til prisene faller.';
      } else {
        bannerMessage = 'Hei! Prisene er på den høye siden nå. Vurder å vente med lading.';
        additionalTip = '💡 Tips: Sjekk prislisten under for å finne rimeligere perioder.';
      }
    } else if (currentPriceValue > 0.30) {
      bannerStyle = 'bg-gray-50 text-gray-800';
      if (nextCheaperHour && waitTimeHours !== null && waitTimeHours < 3) {
        bannerMessage = `Hei! Prisene er OK nå, men blir lavere fra kl ${format(parseISO(nextCheaperHour.time_start), 'HH:mm', { locale: nb })}.`;
        additionalTip = '💡 Tips: Lad nå hvis du må, eller vent til prisene faller for å spare litt ekstra.';
      } else {
        bannerMessage = 'Hei! Strømprisene er på et greit nivå nå. Fint å lade hvis du trenger det.';
        additionalTip = '💡 Tips: Sjekk prislisten under hvis du vil finne enda billigere perioder.';
      }
    }
  } else {
    bannerStyle = 'bg-gray-50 text-gray-800';
    bannerMessage = 'Hei! Jeg henter dagens strømpriser...';
  }

  return (
    <>
      <SEO area={selectedArea} date={selectedDate} />
      <main className="flex-grow container mx-auto px-4 py-8 flex flex-col lg:flex-row">
        {/* Left Sidebar Ad */}
        <aside className="hidden lg:block w-64 lg:mr-8 order-1 lg:order-none">
          <SidebarAd slot="1519579735" />
        </aside>

        {/* Main Content */}
        <div className="flex-1 max-w-5xl w-full mx-auto order-0 lg:order-none">
          <section>
            {/* Day Overview section with ID */}
            <div id="day-overview">
              {/* Google Ad */}
              <div className="mb-6 bg-white dark:bg-gray-800 p-4 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
                <GoogleAd
                  slot="3068465121"
                  format="fluid"
                  layout="in-article"
                  style={{
                    display: 'block',
                    margin: '0 auto',
                    textAlign: 'center',
                    minHeight: '100px'
                  }}
                />
              </div>

            </div>
              <div className="mb-6">
                <PriceAreaSelector 
                  selectedArea={selectedArea} 
                  onAreaChange={setSelectedArea} 
                />
              </div>

              {/* Day Overview with integrated subsidy info */}
              <DayOverview 
                prices={prices} 
                area={selectedArea}
                selectedDate={selectedDate}
                onDateChange={setSelectedDate}
              />
          </section>
          
          {/* Price Chart - immediately after day overview */}
          <section>
            <div className="mb-6 bg-white dark:bg-gray-800 p-4 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
              <GoogleAd 
                slot="3068465121"
                format="fluid"
                layout="in-article"
                style={{ 
                  display: 'block',
                  margin: '0 auto',
                  textAlign: 'center',
                  minHeight: '100px'
                }}
              />
            </div>

            

            
            <div className="bg-white dark:bg-dark-card rounded-lg shadow-sm overflow-hidden mb-6 dark:border dark:border-dark-border" id="price-chart">
              <div className="p-6">
                <PriceChart 
                  prices={prices} 
                  area={selectedArea}
                  selectedDate={selectedDate}
                  onDateChange={setSelectedDate}
                />
              </div>
            </div>
            
            {/* Google Ad */}
            <div className="mb-6 bg-white dark:bg-gray-800 p-4 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
              <GoogleAd
                slot="3068465121"
                format="fluid"
                layout="in-article"
                style={{
                  display: 'block',
                  margin: '0 auto',
                  textAlign: 'center',
                  minHeight: '100px'
                }}
              />
            </div>

            {/* Actionable components - What to do now */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              {/* Best Time To Use Electricity */}
              <BestTimeToUse prices={prices} />

              {/* Morning Report */}
              <MorningReport area={selectedArea} />
            </div>
            
            {/* Detailed price list */}
            <div className="bg-white rounded-lg shadow-sm overflow-hidden mb-6" id="price-list">
              <button
                onClick={() => setIsHeaderExpanded(!isHeaderExpanded)}
                className="w-full px-4 py-4 flex items-center justify-between bg-gradient-to-r from-blue-50 to-blue-100 border-b border-gray-200"
              >
                <div className="flex items-center">
                  <div className="bg-blue-100 p-2 rounded-md mr-3">
                    <Clock className="h-5 w-5 text-blue-600" />
                  </div>
                  <div>
                    <h3 className="text-lg font-medium text-gray-900">Timepriser for dagen</h3>
                  </div>
                </div>
                {isHeaderExpanded ? (
                  <ChevronUp className="h-5 w-5 text-gray-400" />
                ) : (
                  <ChevronDown className="h-5 w-5 text-gray-400" />
                )}
              </button>

              {isHeaderExpanded && <div className="p-6">
                <PriceList 
                  prices={prices} 
                  area={selectedArea}
                  loading={loading} 
                  error={error} 
                />
              </div>}
            </div>

            {/* Weekly trend visualization */}
            <WeeklyTrend area={selectedArea} />
            
            {/* Ad placement before calculator */}

            
            <div className="mb-6 bg-white dark:bg-gray-800 p-4 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
              <GoogleAd 
                slot="3068465121"
                format="fluid"
                layout="in-article"
                style={{ 
                  display: 'block',
                  margin: '0 auto',
                  textAlign: 'center',
                  minHeight: '100px'
                }}
              />
            </div>
            
            {/* Usage Calculator */}
            {currentPriceValue && (
              <div className="mb-6" id="calculator">
                <ElectricityUsageCalculator 
                  currentPrice={currentPriceValue} 
                  priceAfterSubsidy={priceAfterSubsidy}
                  prices={prices}
                  area={selectedArea}
                />
              </div>
            )}

            {/* Information section */}
            <div className="mb-6 bg-white dark:bg-gray-800 p-4 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
              <GoogleAd 
                slot="3068465121"
                format="fluid"
                layout="in-article"
                style={{ 
                  display: 'block',
                  margin: '0 auto',
                  textAlign: 'center',
                  minHeight: '100px'
                }}
              />
            </div>
            
            <div className="bg-white dark:bg-dark-card rounded-lg shadow-sm p-6 mt-10 dark:border dark:border-dark-border">
              <h3 className="text-xl font-medium text-gray-900 dark:text-white mb-4">Om strømprisene</h3>
              <div className="prose prose-blue max-w-none">
                <p className="text-gray-600 dark:text-gray-300">
                  Strømprisene i Norge er delt inn i fem prisområder: Oslo (NO1), Kristiansand (NO2), 
                  Trondheim (NO3), Tromsø (NO4) og Bergen (NO5). Prisene oppdateres hver time, og 
                  publiseres dagen før de trer i kraft.
                </p>
                <p className="text-gray-600 dark:text-gray-300">
                  Prisene som vises er spotpriser fra strømbørsen Nord Pool. Dette er grunnprisen for 
                  strøm, og kommer i tillegg til nettleie og avgifter. Alle priser er oppgitt inkludert 25 % mva.,
                  bortsett fra NO4 (Nord-Norge), der husholdninger er fritatt for mva.
                </p>
                <p className="text-gray-600 dark:text-gray-300">
                  I 2026 dekker strømstøtten 90 % av spotprisen over 77 øre/kWh (uten mva.), time for time, for opptil
                  5000 kWh i måneden. Alternativet er <Link to="/norgespris" className="text-blue-600 dark:text-blue-400 underline">Norgespris</Link> på
                  fast 40 øre/kWh uten mva. Alle som vil ha Norgespris i 2027, må bestille på nytt.
                </p>
              </div>
            </div>
          </section>
        </div>
        
        {/* Right Sidebar Ad */}
        <aside className="hidden lg:block w-64 lg:ml-8 order-2">
          <SidebarAd slot="1519579735" />
        </aside>
      </main>
    </>
  );
}

const App = () => {
  return (
    <BrowserRouter>
      <ScrollToTop />
      <div className="flex flex-col min-h-screen bg-gray-50 dark:bg-dark-bg">
        <Header />
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/blog" element={<Blog />} />
          <Route path="/blog/:slug" element={<BlogPost />} />
          <Route path="/personvernerklaering" element={<PrivacyPolicy />} />
          <Route path="/bruksvilkar" element={<TermsOfService />} />
          <Route path="/norgespris" element={<NorgesPris />} />
          <Route path="/sammenlign-leverandorer" element={<ProviderComparison />} />
          <Route path="/sammenlign-stromleverandorer" element={<Navigate to="/sammenlign-leverandorer" replace />} />
          <Route path="/unsubscribe" element={<Unsubscribe />} />
        </Routes>
        <Footer />
      </div>
    </BrowserRouter>
  );
};

export default App;